import { useRef } from 'react';
import { useInterval } from 'react-use';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import { useDataDog } from '@/reporting/DataDog';
import { marketKey, usePendingOfferPollingStore } from './usePendingOfferPollingStore';

const POLLING_INTERVAL_MS = 5000;

type OfferStatusResponse = paths['/offers/stats/status']['get']['responses']['200']['content']['application/json'];
type OfferStatusRecord = OfferStatusResponse['data'][number];

// supplierDivisionUuid from the API response is the same field as takerDivisionUuid in PendingMarket
const recordKey = (r: OfferStatusRecord): string =>
  marketKey({
    makerOrganizationUuid: r.makerOrganizationUuid,
    takerDivisionUuid: r.supplierDivisionUuid,
    marketUuid: r.marketUuid,
  });

const useOfferStatusPolling = (): void => {
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const hasLoggedPollingError = useRef(false);
  const { refetchManyTakerMarkets } = useRefetchStatsQueries();
  const { getPendingMarkets, removePendingMarkets, hasPendingMarkets } = usePendingOfferPollingStore();

  useInterval(async () => {
    if (!hasPendingMarkets()) {
      return;
    }

    try {
      const pendingMarkets = getPendingMarkets();

      const response = await apiClient.get('api/supplier-experience/offers/stats/status').json<OfferStatusResponse>();
      hasLoggedPollingError.current = false;

      // The endpoint only returns markets still PENDING — absence means resolved
      const responseKeys = new Set(response.data.map(recordKey));
      const resolvedMarkets = pendingMarkets.filter((m) => !responseKeys.has(marketKey(m)));

      if (resolvedMarkets.length === 0) {
        return;
      }

      const now = Date.now();
      resolvedMarkets.forEach((m) => {
        trackEnterpriseEvent('polling-update::long', {
          marketUuid: m.marketUuid,
          takerDivisionUuid: m.takerDivisionUuid,
          makerOrganizationUuid: m.makerOrganizationUuid,
          durationMs: now - m.enqueuedAt,
        });
      });

      removePendingMarkets(resolvedMarkets);

      await refetchManyTakerMarkets(
        resolvedMarkets.map((m) => ({
          marketUuid: m.marketUuid,
          takerUuid: m.takerDivisionUuid,
        }))
      );
    } catch (error) {
      // Polling errors are transient — next interval tick will retry.
      // Only log once until a successful poll resets the flag, to avoid flooding DataDog.
      // AbortError means the browser cancelled the request — not a real failure.
      if ((error as { name?: string }).name === 'AbortError') {
        return;
      }

      if (!hasLoggedPollingError.current) {
        addError(error, { source: 'useOfferStatusPolling' });
        hasLoggedPollingError.current = true;
      }
    }
  }, POLLING_INTERVAL_MS);
};

export default useOfferStatusPolling;
